import React, { useCallback, useMemo } from 'react';
import UnifiedCheckoutOfferView from './UnifiedCheckoutOfferView';
import { RouterProps } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { selectPrequalOffer } from 'state/prequalOffer';
import useQueryParameters from 'hooks/useQueryParameters';
import { selectFinanceOffersByType } from 'state/financeOffers';
import {
  selectCreditApplication,
  mergeApplication,
} from 'state/creditApplication';
import { isDecimalMoney } from 'utils/validations';
import * as Yup from 'yup';
import FmcForm from 'components/FmcForm';
import { selectTenant } from 'state/tenant';
import { selectInstantCredit } from 'state/instantCredit';
import {
  ExpandedPanel,
  selectExpandedPanel,
  setExpandedPanel,
} from 'state/unifiedWidgets';
import useTranslate from 'hooks/useTranslate';
import useWorkflow, { Step } from 'hooks/useWorkflow';

type PropTypes = RouterProps;

export interface FormValues {
  intendedPurchaseAmount: string | null;
}

const UnifiedCheckoutOffer: React.FC<PropTypes> = () => {
  const dispatch = useDispatch();
  const { bread } = useSelector(selectTenant);
  const t = useTranslate();
  if (!bread) {
    // Should never happens
    throw new Error('Invalid tenant config');
  }

  const { pathAfter, steps } = useWorkflow();

  const prequalOffer = useSelector(selectPrequalOffer);
  const { intendedPurchaseAmount, selectedCardKey } = useSelector(
    selectCreditApplication,
  );

  const { installmentOffers, splitPayOffers } = useSelector(
    selectFinanceOffersByType,
  );

  const instantCredit = useSelector(selectInstantCredit);
  const expandedPanel = useSelector(selectExpandedPanel);

  const { firstOffers } = useQueryParameters();

  const schema = useMemo(() => {
    return Yup.object().shape({
      intendedPurchaseAmount: Yup.string()
        .nullable()
        .required(t('unified.offer.intendedPurchaseAmountRequired'))
        .concat(isDecimalMoney),
    });
  }, [t]);

  const initialValues = useMemo(() => ({ intendedPurchaseAmount }), [
    intendedPurchaseAmount,
  ]);

  const handleSubmit = useCallback(
    (values: FormValues) => {
      dispatch(mergeApplication(schema.cast(values)));
    },
    [dispatch, schema],
  );

  const handlePanelExpandChange = useCallback(
    (
      _event: React.ChangeEvent<{}>,
      panelName: string,
      newExpanded: boolean,
    ) => {
      if (newExpanded) {
        dispatch(setExpandedPanel(panelName as ExpandedPanel));
      } else {
        dispatch(setExpandedPanel(undefined));
      }
    },
    [dispatch],
  );

  const nextPath = useMemo(() => {
    const step =
      selectedCardKey && steps.includes(Step.SECOND_CARD_SELECTOR)
        ? Step.SECOND_CARD_SELECTOR
        : Step.UNIFIED_PREQUAL_CHECKOUT_OFFER;

    return steps[steps.length - 1] !== step ? pathAfter(step) : ''; // check if we are on the last step already
  }, [pathAfter, selectedCardKey, steps]);

  return (
    <FmcForm
      onSubmit={handleSubmit}
      validationSchema={schema}
      initialValues={initialValues}
    >
      {formProps => {
        const { intendedPurchaseAmount } = schema.cast(formProps.values);
        return (
          <UnifiedCheckoutOfferView
            intendedPurchaseAmount={intendedPurchaseAmount}
            formikProps={formProps}
            installmentOffers={installmentOffers}
            splitPayOffers={splitPayOffers}
            prequalOffer={prequalOffer}
            onPanelChange={handlePanelExpandChange}
            expandedPanel={expandedPanel}
            defaultCartAmount={bread.defaultCartAmount}
            firstOffers={firstOffers}
            openToBuy={instantCredit.openToBuy}
            nextPath={nextPath}
          />
        );
      }}
    </FmcForm>
  );
};

export default UnifiedCheckoutOffer;
